import React from 'react';
import { Box, Typography, Button, Divider, Grid, Tooltip, Chip, useTheme } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FolderIcon from '@mui/icons-material/Folder';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PremiumCard from '../../ui/PremiumCard';
import StatusBadge from '../../ui/StatusBadge';
import { IBackupTarefaResumo } from '../types';
import { formatarTamanhoBytes, formatarDataHora } from '../utils';

interface CardResumoTarefaProps {
  tarefa: IBackupTarefaResumo;
  onVerArquivos: (tarefaAgendadaID: string, nomeTarefa: string) => void;
  onVerLogs: (tarefaAgendadaID: string, nomeTarefa: string) => void;
  onBaixarUltimo: (arquivoID: string, nomeArquivo: string) => void;
  baixandoArquivoID: string | null;
}

export const CardResumoTarefa: React.FC<CardResumoTarefaProps> = ({
  tarefa,
  onVerArquivos,
  onVerLogs,
  onBaixarUltimo,
  baixandoArquivoID
}) => {
  const {
    tarefaAgendadaID,
    nome,
    tipo,
    grupo,
    statusOperacional,
    totalArquivos,
    tamanhoTotalBytes,
    ultimoBackup,
    ultimaFalha
  } = tarefa;

  const theme = useTheme();
  const isBaixando = !!(ultimoBackup && baixandoArquivoID === ultimoBackup.arquivoID);

  return (
    <PremiumCard sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
            <Tooltip title={nome}>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, mb: 1.5, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {nome}
              </Typography>
            </Tooltip>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', minHeight: '24px' }}>
              <Chip
                label={grupo || 'Sem Grupo'}
                size="small"
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(51, 65, 85, 0.5)' : 'rgba(0, 0, 0, 0.04)',
                  color: 'text.secondary',
                  border: `1px solid ${theme.palette.divider}`,
                  fontSize: '0.75rem'
                }}
              />
              <Chip
                label={tipo === 'CheckDB' ? 'Diagnóstico' : (tipo || 'Arquivos')}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
            </Box>
          </Box>
          <StatusBadge status={statusOperacional} />
        </Box>

        <Divider sx={{ borderColor: 'divider', my: 1.5 }} />

        {/* Métricas */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon sx={{ color: '#38BDF8', fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                  Arquivos
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {totalArquivos}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon sx={{ color: '#A78BFA', fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                  Tamanho Total
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {formatarTamanhoBytes(tamanhoTotalBytes)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Informações de Status */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {/* Último Backup com Sucesso */}
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.4)' : 'rgba(0, 0, 0, 0.02)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              p: 1.5,
              minHeight: '74px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: '1rem' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Último backup bem-sucedido
              </Typography>
            </Box>
            {ultimoBackup ? (
              <Box>
                <Tooltip title={ultimoBackup.nomeArquivo}>
                  <Typography variant="body2" sx={{ color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {ultimoBackup.nomeArquivo}
                  </Typography>
                </Tooltip>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {formatarDataHora(ultimoBackup.dataHora)} • {formatarTamanhoBytes(ultimoBackup.tamanhoBytes)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.85rem' }}>
                  Nenhum backup encontrado
                </Typography>
                <Typography variant="caption" sx={{ visibility: 'hidden', display: 'block' }}>
                  -
                </Typography>
              </Box>
            )}
          </Box>

          {/* Última Falha (se houver) */}
          {ultimaFalha && (
            <Box
              sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                p: 1.5,
                minHeight: '74px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: '1rem' }} />
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                  Última execução falhou
                </Typography>
              </Box>
              <Tooltip title={ultimaFalha.mensagem}>
                <Typography variant="body2" sx={{ color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {ultimaFalha.mensagem}
                </Typography>
              </Tooltip>
              <Typography variant="caption" sx={{ color: 'error.main', opacity: 0.8, display: 'block' }}>
                {formatarDataHora(ultimaFalha.dataHora)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Ações */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onVerArquivos(tarefaAgendadaID, nome)}
            sx={{
              flex: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              textTransform: 'none',
              height: '36px',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(42, 81, 247, 0.04)',
                color: 'primary.main'
              }
            }}
          >
            Arquivos
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onVerLogs(tarefaAgendadaID, nome)}
            sx={{
              flex: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              textTransform: 'none',
              height: '36px',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(42, 81, 247, 0.04)',
                color: 'primary.main'
              }
            }}
          >
            Logs
          </Button>

          <Tooltip title={ultimoBackup ? "Baixar último backup concluído" : "Nenhum backup disponível para download"}>
            <span>
              <Button
                variant={ultimoBackup ? "contained" : "outlined"}
                color="primary"
                size="small"
                disabled={!ultimoBackup || isBaixando}
                onClick={() => ultimoBackup && onBaixarUltimo(ultimoBackup.arquivoID, ultimoBackup.nomeArquivo)}
                sx={{
                  minWidth: '40px',
                  width: '40px',
                  height: '36px',
                  p: 0,
                  boxShadow: 'none',
                  borderColor: 'divider',
                  color: ultimoBackup ? '#fff' : 'text.disabled',
                  '&:hover': { boxShadow: 'none' }
                }}
              >
                <CloudDownloadIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </PremiumCard>
  );
};

export default CardResumoTarefa;
